import React, { useState, useEffect, useRef } from 'react';
import {
    FormGroup,
    FormControlLabel,
    Checkbox,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Grid,
    TextField
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { FormLabel, RadioGroup, FormControl, Radio } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import CrudButtons from '../../../GlobalFunction/CrudButtons';

// Custom styled TextField component for consistent styling
const CustomTextField = styled(TextField)(({ theme }) => ({
    '& .MuiFilledInput-root': {
        backgroundColor: 'transparent',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
    },
    '& .Mui-focused': {
        borderColor: '#673ab7',
    },
    '& .MuiFilledInput-root.Mui-focused': {
        border: '1px solid #673ab7',
    },
}));

// Definition of table columns for the asset transfer table
const tableColumns = [
    { id: 'SgrnNo', label: 'GRN-No', minWidth: 50 },
    { id: 'VendName', label: 'Vendor', minWidth: 50 },
    { id: 'BillDt', label: 'Date', minWidth: 50 },
    { id: 'BillNo', label: 'BillNo', minWidth: 50 },
    { id: 'ItmName', label: 'Item', minWidth: 50 },
    { id: 'Qty', label: 'Qty', minWidth: 50 },
    { id: 'Rate', label: 'Rate', minWidth: 50 },
];

const AssetTransfer = () => {
    const [mode, setMode] = useState('view');
    const [isFormDisabled, setIsFormDisabled] = useState(true);
    const [currentEnqId, setCurrentEnqId] = useState(null);
    const [formData, setFormData] = useState({
        PropId: "",
        SgrnId: "",
        SgrnDt: moment().format('DD/MM/YYYY'),
        ItmId: "",
        Qty: "",
        Rate: "",
        ModelNo: "",
        ModelBrand: "",
        ModelColour: "",
        ModelClass: "",
        ModelOemPartNo: "",
        WarrantyPeriod: "",
        WarrantyUpto: "",
        ExpLife: "",
        RelationId: "",
        MaintFreq: "",
        DepAccLedId: "",
        DepRate: "",
        Remark: "",
        Status: "1",
        assetLocation: "",
    });
    const [tableData, setTableData] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
    const [internalMode, setInternalMode] = useState("add");
    const [addDisabled, setAddDisabled] = useState(false);
    const [allButtonDisabled, setAllButtonDisabled] = useState(true);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [fetchedData, setFetchedData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isFormEditable, setIsFormEditable] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const contentRef = useRef(null);
    const handlePrint = useReactToPrint({ contentRef });

    // Function names and logic copied from AssetAllocation
    const handlePrevious = () => { };
    const handleNext = () => { };
    const handleClickAdd = () => {
        setInternalMode("add");
        fetchDialogData(); // Placeholder for fetching dialog data
        setOpenDialog(true);
    };
    const handleClickEdit = () => {
        if (selectedRowIndex >= 0) {
            setInternalMode("edit");
            setIsFormEditable(true);
            setIsFormDisabled(false);
            setAllButtonDisabled(true);
            setAddDisabled(true);

            const selectedRow = tableData[selectedRowIndex];
            setFormData({
                ...formData,
                PropId: selectedRow.PropId || "",
                SgrnId: selectedRow.SgrnId || "",
                SgrnDt: selectedRow.SgrnDt || moment().format('DD/MM/YYYY'),
                ItmId: selectedRow.ItmId || "",
                Qty: selectedRow.Qty || "",
                Rate: selectedRow.Rate || "",
                ModelNo: selectedRow.ModelNo || "",
                ModelBrand: selectedRow.ModelBrand || "",
                ModelColour: selectedRow.ModelColour || "",
                ModelClass: selectedRow.ModelClass || "",
                ModelOemPartNo: selectedRow.ModelOemPartNo || "",
                WarrantyPeriod: selectedRow.WarrantyPeriod || "",
                WarrantyUpto: selectedRow.WarrantyUpto || "",
                ExpLife: selectedRow.ExpLife || "",
                RelationId: selectedRow.RelationId || "",
                MaintFreq: selectedRow.MaintFreq || "",
                DepAccLedId: selectedRow.DepAccLedId || "",
                DepRate: selectedRow.DepRate || "",
                Remark: selectedRow.Remark || "",
                Status: selectedRow.Status || "1"
            });
        } else {
            toast.error("Please select a row to edit.");
        }
    };
    const handleClickConfirm = () => {
        const requiredFields = ["ItmId", "Qty", "Rate"];
        if (requiredFields.some(field => !formData[field])) {
            toast.error("Please fill all required fields");
            return;
        }

        const updatedItem = {
            ...formData,
            DBFLAG: internalMode === "add" ? "I" : "U"
        };

        if (internalMode === "add") {
            if (!tableData.some(item => item.ItmId === updatedItem.ItmId && item.DBFLAG !== "D")) {
                setTableData([...tableData, updatedItem]);
                setSelectedRowIndex(tableData.length);
            } else {
                toast.error("This item already exists in the table");
                return;
            }
        } else {
            const updatedTableData = [...tableData];
            updatedTableData[selectedRowIndex] = {
                ...updatedTableData[selectedRowIndex],
                ...updatedItem,
                DBFLAG: "U"
            };
            setTableData(updatedTableData);
        }

        setIsFormDisabled(true);
        setIsFormEditable(false);
        setAddDisabled(true);
        setAllButtonDisabled(false);
    };
    const handleClickCancel = () => {
        setIsFormDisabled(true);
        setIsFormEditable(false);
        setAddDisabled(true);
        setAllButtonDisabled(false);
    };
    const handleDelete = () => {
        if (selectedRowIndex >= 0) setOpenDeleteDialog(true);
        else toast.error("Please select a row to delete.");
    };
    const handleConfirmDelete = () => {
        const updatedTableData = [...tableData];
        const itemFlag = updatedTableData[selectedRowIndex].DBFLAG;
        if (itemFlag === "U" || itemFlag === "R") {
            updatedTableData[selectedRowIndex].DBFLAG = "D";
        } else {
            updatedTableData.splice(selectedRowIndex, 1);
        }
        setTableData(updatedTableData);
        setOpenDeleteDialog(false);
        setSelectedRowIndex(updatedTableData.length > 0 ? 0 : -1);
        setAllButtonDisabled(false);
        setAddDisabled(false);
        setIsFormDisabled(true);
    };
    const handleCheckboxChange = (event, row) => {
        if (event.target.checked) {
            setSelectedRows([row]);
        } else {
            setSelectedRows([]);
        }
    };
    const handleFilterChange = () => { }; // Placeholder

    const handleConfirmSelection = () => {
        if (selectedRows.length === 0) {
            toast.error("Please select at least one record.");
            return;
        }
        if (selectedRows.length > 1) {
            toast.error("You can only select one item at a time.");
            return;
        }

        const newTableData = [...tableData];
        selectedRows.forEach((row) => {
            if (!newTableData.some((existingRow) => existingRow.ItmId === row.ItmId && existingRow.DBFLAG !== "D")) {
                const newRow = {
                    SgrnNo: row.SgrnNo || "",
                    SgrnId: row.SgrnId || "",
                    SgrnDt: row.SgrnDt || "",
                    ItmId: row.ItmId || "",
                    VendId: row.VendId || "",
                    VendName: row.VendName || "",
                    BillNo: row.BillNo || "",
                    BillDt: row.BillDt || "",
                    Qty: row.Qty || "1",
                    Rate: row.Rate || "0",
                    ModelNo: row.ModelNo || "",
                    ModelBrand: row.ModelBrand || "",
                    ModelColour: row.ModelColour || "",
                    ModelClass: row.ModelClass || "",
                    ModelOemPartNo: row.ModelOemPartNo || "",
                    WarrantyPeriod: row.WarrantyPeriod || "",
                    WarrantyUpto: row.WarrantyUpto || "",
                    ExpLife: row.ExpLife || "",
                    RelationId: row.RelationId || "",
                    MaintFreq: row.MaintFreq || "",
                    DepAccLedId: row.DepAccLedId || "",
                    DepRate: row.DepRate || "",
                    Remark: row.Remark || "",
                    Status: row.Status || "1",
                    DBFLAG: 'I'
                };
                newTableData.push(newRow);
            } else {
                toast.warning(`Item ${row.ItmName} is already added.`);
            }
        });

        setTableData(newTableData);
        setOpenDialog(false);
        setSelectedRows([]);
        setAddDisabled(false);
        setAllButtonDisabled(false);
    };

    const handleSubmit = () => {
        // Placeholder for submit logic
        toast.success("Submit functionality to be implemented");
    };

    const handleAdd = () => {
        setMode('add');
        setIsFormDisabled(false);
        setIsFormEditable(true);
        setAddDisabled(false);
        setAllButtonDisabled(true);

        setFormData({
            PropId: "",
            SgrnId: "",
            SgrnDt: moment().format('DD/MM/YYYY'),
            ItmId: "",
            Qty: "",
            Rate: "",
            ModelNo: "",
            ModelBrand: "",
            ModelColour: "",
            ModelClass: "",
            ModelOemPartNo: "",
            WarrantyPeriod: "",
            WarrantyUpto: "",
            ExpLife: "",
            RelationId: "",
            MaintFreq: "",
            DepAccLedId: "",
            DepRate: "",
            Remark: "",
            Status: "1",
            assetLocation: ""
        });
        setSelectedRowIndex(-1);
    };

    const handleEdit = () => {
        setMode('edit');
        setIsFormDisabled(false);
        setAddDisabled(false);
        setAllButtonDisabled(false);
    };

    const handleCancel = () => {
        if (currentEnqId) {
            setMode('view');
            setIsFormDisabled(true);
            // Placeholder for fetchAssetData(currentEnqId, "R");
        } else {
            setMode('view');
            setIsFormDisabled(true);
            setFormData({
                PropId: "",
                SgrnId: "",
                SgrnDt: moment().format('DD/MM/YYYY'),
                ItmId: "",
                Qty: "",
                Rate: "",
                ModelNo: "",
                ModelBrand: "",
                ModelColour: "",
                ModelClass: "",
                ModelOemPartNo: "",
                WarrantyPeriod: "",
                WarrantyUpto: "",
                ExpLife: "",
                RelationId: "",
                MaintFreq: "",
                DepAccLedId: "",
                DepRate: "",
                Remark: "",
                Status: "1",
                assetLocation: ""
            });
            setTableData([]);
        }
        setSelectedRowIndex(-1);
    };
    const handleExit = () => {
        navigate('/assettransfertable'); // Adjust the route as needed
    };
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(URL.createObjectURL(file));
        }
    };

    const handleChangeStatus = () => { };

    // Placeholder for fetching dialog data
    const fetchDialogData = () => {
        // Implement API call as needed
        setFetchedData([]); // Placeholder
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleLocationRadioChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({
            ...prev,
            assetLocation: value
        }));
    };

    return (
        <>
            <ToastContainer />
            <Box className="form-container">
                <Grid container spacing={2} className="form_grid" sx={{
                    width: '97% !important',
                    height: 'auto',
                    margin: '20px !important'
                }}>
                    {/* Header section */}
                    <Grid item xs={12} className="form_title" sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "20px",
                        flexDirection: { xs: "column", sm: "row", md: "row" },
                    }}>
                        <Grid sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', sm: 'flex-start' },
                            marginBottom: { xs: '10px', sm: 0 },
                        }}>
                            <Button
                                variant="contained"
                                size="small"
                                className="three-d-button-previous"
                                onClick={handlePrevious}
                                sx={{ backgroundColor: '#635BFF', marginRight: { xs: '5px', sm: '10px' }, display: { xs: 'none', sm: 'block' } }}
                                disabled={mode !== 'view' || !currentEnqId || currentEnqId === 1}
                            >
                                <KeyboardArrowLeftIcon />
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                className="three-d-button-next"
                                onClick={handleNext}
                                sx={{ backgroundColor: '#635BFF', display: { xs: 'none', sm: 'block' } }}
                                disabled={mode !== 'view' || !currentEnqId}
                            >
                                <NavigateNextIcon />
                            </Button>
                        </Grid>

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            flexGrow: 1,
                            justifyContent: 'center',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h5" sx={{
                                textAlign: "center",
                                fontSize: { xs: "24px", sm: "18px", md: "24px" },
                                whiteSpace: 'nowrap'
                            }}>
                                Asset Transfer
                            </Typography>
                            <Box sx={{
                                minWidth: '200px',
                                maxWidth: '250px',
                                '& .MuiAutocomplete-root': { paddingTop: '0px', paddingBottom: '0px' },
                                '& .MuiFormControl-root': { marginTop: '0px', marginBottom: '0px' },
                                '& .MuiInputBase-root': { height: '40px' }
                            }}>
                                {/* Placeholder for Property Dropdown */}
                            </Box>
                        </Box>

                        <Grid sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "10px",
                            flexWrap: "wrap"
                        }}>
                            <Box sx={{
                                minWidth: "220px",
                                '& .MuiFormControl-root': { margin: 0 },
                                '& .MuiInputBase-root': { height: "36px" }
                            }}>
                                {/* Placeholder for Property Autocomplete */}
                            </Box>
                            <CrudButtons
                                moduleName="Asset Transfer"
                                mode={mode}
                                onAdd={handleAdd}
                                onEdit={handleEdit}
                                onView={handlePrint}
                                onExit={handleExit}
                                readOnlyMode={isFormDisabled}
                            />
                        </Grid>
                    </Grid>

                    {/* Table section */}
                    <Grid item xs={12}>
                        <Paper sx={{ width: "90%", overflow: "hidden", margin: "0px auto 10px auto", border: "1px solid lightgray" }}>
                            <TableContainer sx={{ maxHeight: 300 }}>
                                <Table
                                    stickyHeader
                                    size="small"
                                    sx={{
                                        '& .MuiTableCell-root': { padding: '1.4px 3px', lineHeight: '1.2' },
                                        '& .MuiTableRow-root': { height: '0px' }
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {tableColumns.map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    style={{
                                                        minWidth: column.minWidth,
                                                        padding: '0px',
                                                        backgroundColor: '#f5f5f5'
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" fontWeight="bold">{column.label}</Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.filter(row => row.DBFLAG !== "D").map((row, index) => (
                                            <TableRow
                                                hover
                                                key={index}
                                                onClick={() => {
                                                    setSelectedRowIndex(index);
                                                    setFormData({
                                                        ...formData,
                                                        PropId: row.PropId || "",
                                                        SgrnId: row.SgrnId || "",
                                                        SgrnDt: row.SgrnDt || moment().format('DD/MM/YYYY'),
                                                        ItmId: row.ItmId || "",
                                                        Qty: row.Qty || "",
                                                        Rate: row.Rate || "",
                                                        ModelNo: row.ModelNo || "",
                                                        ModelBrand: row.ModelBrand || "",
                                                        ModelColour: row.ModelColour || "",
                                                        ModelClass: row.ModelClass || "",
                                                        ModelOemPartNo: row.ModelOemPartNo || "",
                                                        WarrantyPeriod: row.WarrantyPeriod || "",
                                                        WarrantyUpto: row.WarrantyUpto || "",
                                                        ExpLife: row.ExpLife || "",
                                                        RelationId: row.RelationId || "",
                                                        MaintFreq: row.MaintFreq || "",
                                                        DepAccLedId: row.DepAccLedId || "",
                                                        DepRate: row.DepRate || "",
                                                        Remark: row.Remark || "",
                                                        Status: row.Status || "1"
                                                    });
                                                }}
                                                selected={selectedRowIndex === index}
                                                sx={{
                                                    '&.Mui-selected': { backgroundColor: '#e3f2fd' },
                                                    '&.Mui-selected:hover': { backgroundColor: '#bbdefb' }
                                                }}
                                            >
                                                {tableColumns.map((column) => (
                                                    <TableCell key={column.id}>
                                                        {row[column.id] || "N/A"}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Form fields and Asset Location moved closer to table */}
                    <Grid item xs={12} md={12} lg={12} className="form_field" sx={{ marginInline: { xs: "10px", sm: "80px" }, marginTop: '10px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6} lg={6}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ItmId"
                                                    name="ItmId"
                                                    label={<span>Item Name<span style={{ color: "red" }}>*</span></span>}
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ItmId}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ItemCategory"
                                                    name="ItemCategory"
                                                    label={<span>Item Category<span style={{ color: "red" }}>*</span></span>}
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ItemCategory || ""}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ItemGroup"
                                                    name="ItemGroup"
                                                    label={<span>Item Group<span style={{ color: "red" }}>*</span></span>}
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ItemGroup || ""}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ItemSubgroup"
                                                    name="ItemSubgroup"
                                                    label={<span>Item Subgroup<span style={{ color: "red" }}>*</span></span>}
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ItemSubgroup || ""}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ModelNo"
                                                    name="ModelNo"
                                                    label="Model No"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ModelNo}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ModelBrand"
                                                    name="ModelBrand"
                                                    label="Brand"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ModelBrand}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ModelColour"
                                                    name="ModelColour"
                                                    label="Color"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ModelColour}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ModelClass"
                                                    name="ModelClass"
                                                    label="Class"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ModelClass}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ModelOemPartNo"
                                                    name="ModelOemPartNo"
                                                    label="Oem Part No"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ModelOemPartNo}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="WarrantyPeriod"
                                                    name="WarrantyPeriod"
                                                    label="Warranty Period"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.WarrantyPeriod}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="WarrantyUpto"
                                                    name="WarrantyUpto"
                                                    label="Warranty Upto"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.WarrantyUpto}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="ExpLife"
                                                    name="ExpLife"
                                                    label="Exp Life"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.ExpLife}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={3}>
                                                {/* Placeholder for Service Provider Autocomplete */}
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="MaintFreq"
                                                    name="MaintFreq"
                                                    label="Maintenance Freq"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.MaintFreq}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                {/* Placeholder for Ledger Autocomplete */}
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="DepRate"
                                                    name="DepRate"
                                                    label="Dep Rate"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.DepRate}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="Remark"
                                                    name="Remark"
                                                    label="Remark"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.Remark}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="Qty"
                                                    name="Qty"
                                                    label="Qty"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.Qty}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    id="Rate"
                                                    name="Rate"
                                                    label="Rate"
                                                    variant="filled"
                                                    fullWidth
                                                    className="custom-textfield"
                                                    value={formData.Rate}
                                                    onChange={handleInputChange}
                                                    disabled={!isFormEditable}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleClickConfirm}
                                                        disabled={!isFormEditable}
                                                        sx={{ backgroundColor: '#635bff', '&:hover': { backgroundColor: '#4a2fdb' } }}
                                                    >
                                                        Confirm
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleClickCancel}
                                                        disabled={!isFormEditable}
                                                        sx={{ backgroundColor: '#635bff', '&:hover': { backgroundColor: '#4a2fdb' } }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6} lg={6}>
                                <Paper elevation={2} sx={{
                                    p: 2,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 3,
                                    backgroundColor: '#f9f9f9'
                                }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle1" sx={{
                                            fontWeight: 'bold',
                                            color: '#635BFF',
                                            mb: 1,
                                            borderBottom: '1px solid #e0e0e0',
                                            pb: 0.5
                                        }}>
                                            Asset Location
                                        </Typography>
                                        <FormControl component="fieldset" fullWidth>
                                            <RadioGroup
                                                aria-label="asset-location"
                                                name="assetLocation"
                                                value={formData.assetLocation || ''}
                                                onChange={handleLocationRadioChange}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    flexWrap: 'wrap',
                                                    gap: '8px',
                                                }}
                                            >
                                                <FormControlLabel
                                                    value="Unit"
                                                    control={<Radio size="small" disabled={!isFormEditable} sx={{ color: '#635BFF', '&.Mui-checked': { color: '#635BFF' } }} />}
                                                    label="Unit"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                                                />
                                                <FormControlLabel
                                                    value="Common Area"
                                                    control={<Radio size="small" disabled={!isFormEditable} sx={{ color: '#635BFF', '&.Mui-checked': { color: '#635BFF' } }} />}
                                                    label="Common Area"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                                                />
                                                <FormControlLabel
                                                    value="Amenity"
                                                    control={<Radio size="small" disabled={!isFormEditable} sx={{ color: '#635BFF', '&.Mui-checked': { color: '#635BFF' } }} />}
                                                    label="Amenity"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                                                />
                                                <FormControlLabel
                                                    value="Floor"
                                                    control={<Radio size="small" disabled={!isFormEditable} sx={{ color: '#635BFF', '&.Mui-checked': { color: '#635BFF' } }} />}
                                                    label="Floor"
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Submit/Cancel buttons */}
                    <Grid item xs={12} className="form_button">
                        {mode === 'view' && (
                            <>
                                <Button
                                    variant="contained"
                                    sx={{ mr: 1, background: 'linear-gradient(290deg, #d4d4d4, #ffffff)' }}
                                    onClick={handleAdd}
                                    disabled
                                >
                                    Submit
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{ mr: 1, background: 'linear-gradient(290deg, #a7c5e9, #ffffff)' }}
                                    onClick={handleEdit}
                                    disabled
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                        {(mode === 'edit' || mode === 'add') && (
                            <>
                                <Button variant="contained" sx={{ mr: 1 }} onClick={handleSubmit}>
                                    Submit
                                </Button>
                                <Button variant="contained" sx={{ mr: 1 }} onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Grid>

                    {/* Selection dialog */}
                    {openDialog && (
                        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
                            <DialogTitle>Select Records</DialogTitle>
                            <DialogContent sx={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                paddingBottom: '10px'
                            }}>
                                <TableContainer sx={{
                                    maxHeight: '55vh',
                                    width: '100%',
                                    '& .MuiTableCell-root': { padding: '4px 6px', lineHeight: '1.1', fontSize: '0.85rem' },
                                    '& .MuiTableRow-root': { height: '2px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }
                                }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#f5f5f5', padding: '5px', fontWeight: 'bold' } }}>
                                                <TableCell align="center" sx={{ width: '50px' }}>Select</TableCell>
                                                {tableColumns.map((column) => (
                                                    <TableCell key={column.id} align="center" sx={{ minWidth: column.minWidth }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                            <Typography variant="subtitle2">{column.label}</Typography>
                                                            <TextField
                                                                size="small"
                                                                variant="outlined"
                                                                onChange={(e) => handleFilterChange(column.id, e.target.value)}
                                                                sx={{ marginTop: '4px', '& .MuiInputBase-root': { height: '28px' } }}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {fetchedData.map((row, index) => (
                                                <TableRow key={`row-${index}`}>
                                                    <TableCell align="center">
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedRows.some(r => r.ItmId === row.ItmId)}
                                                            onChange={(e) => handleCheckboxChange(e, row)}
                                                            disabled={selectedRows.length > 0 && !selectedRows.some(r => r.ItmId === row.ItmId)}
                                                        />
                                                    </TableCell>
                                                    {tableColumns.map((column) => (
                                                        <TableCell key={`${column.id}-${index}`} align="center">
                                                            {row[column.id] || "N/A"}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </DialogContent>
                            <DialogActions sx={{ padding: '8px 16px', borderTop: '1px solid #e0e0e0' }}>
                                <Button onClick={handleConfirmSelection} size="small" sx={{ minWidth: '80px' }} disabled={selectedRows.length === 0}>Confirm</Button>
                                <Button onClick={() => { setOpenDialog(false); setSelectedRows([]); }} size="small" sx={{ minWidth: '80px' }}>Cancel</Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </Grid>
            </Box>

            {/* Delete confirmation dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this record?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button sx={{ backgroundColor: '#635bff', color: 'white', '&:hover': { backgroundColor: '#1565c0' } }} onClick={handleConfirmDelete}>Yes</Button>
                    <Button sx={{ backgroundColor: '#635bff', color: 'white', '&:hover': { backgroundColor: '#1565c0' } }} onClick={() => setOpenDeleteDialog(false)}>No</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default AssetTransfer;